/**
 * Staff → SMS Templates admin page.
 *
 * List / create / edit / archive reusable SMS bodies. Templates feed the
 * dropdown on the "Send SMS" modal (SendMessageModal).
 *
 * Placeholders like `{{first_name}}` are typed directly into the body and
 * previewed live below the textarea. Save is blocked if the body exceeds
 * 480 chars (3 SMS segments) — mirrors the backend's cap so we never
 * surface a 422.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Save, X, MessageSquare, RefreshCcw, EyeOff, Wand2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_SUGGESTIONS = ['General', 'Deliveries', 'Payments', 'Support', 'Follow-up', 'Broadcast'];
const PLACEHOLDER_SUGGESTIONS = ['first_name', 'last_name', 'company', 'order_ref', 'invoice_no', 'amount', 'due_date', 'link'];
const MAX_BODY_CHARS = 480;

function authHeaders() {
  return { 'Content-Type': 'application/json' };
}

async function fetchTemplates(includeArchived = false) {
  const res = await fetch(
    `${API_URL}/api/messages/sms-templates?include_archived=${includeArchived ? 1 : 0}`,
    { credentials: 'include', headers: authHeaders() },
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export default function StaffSmsTemplates() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [editing, setEditing] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      setTemplates(await fetchTemplates(includeArchived));
    } catch (e) {
      toast.error('Could not load templates', { description: String(e).slice(0, 200) });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [includeArchived]);

  const grouped = useMemo(() => {
    const g = {};
    for (const t of templates) {
      const key = t.category || 'Uncategorised';
      (g[key] ||= []).push(t);
    }
    return g;
  }, [templates]);

  const archive = async (t) => {
    if (!window.confirm(`Archive "${t.name}"?`)) return;
    const res = await fetch(`${API_URL}/api/messages/sms-templates/${t.id}`, {
      method: 'DELETE', credentials: 'include', headers: authHeaders(),
    });
    if (res.ok) { toast.success(`Archived "${t.name}"`); reload(); }
    else toast.error('Archive failed');
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="staff-sms-templates-page">
      <div className="max-w-4xl mx-auto p-4 sm:p-6">
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-200 rounded-lg" data-testid="staff-sms-templates-back">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-[200px]">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-blue-600" />
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">SMS Templates</h1>
            </div>
            <p className="text-sm text-gray-500 mt-0.5">
              Reusable text bodies for the compose dropdown. Use <code className="bg-gray-100 rounded px-1 text-xs">{'{{first_name}}'}</code> to insert per-recipient variables.
            </p>
          </div>
          <button
            onClick={() => setEditing({ _mode: 'new', name: '', body: '', category: '' })}
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-3 py-2 text-sm font-semibold"
            data-testid="staff-sms-templates-new-btn"
          >
            <Plus className="w-4 h-4" /> New template
          </button>
        </div>

        <div className="flex items-center gap-2 mb-3 text-xs text-gray-600">
          <label className="inline-flex items-center gap-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
              data-testid="staff-sms-templates-toggle-archived"
            />
            Include archived
          </label>
          <button onClick={reload} className="ml-auto inline-flex items-center gap-1 text-gray-500 hover:text-gray-800">
            <RefreshCcw className="w-3 h-3" /> Refresh
          </button>
        </div>

        {loading ? (
          <div className="text-sm text-gray-500">Loading…</div>
        ) : templates.length === 0 ? (
          <div className="bg-white border border-dashed border-gray-300 rounded-xl p-8 text-center text-gray-500 text-sm">
            No templates yet. Click <strong>New template</strong> to add the first one — e.g. a delivery-day notification.
          </div>
        ) : (
          <div className="space-y-4">
            {Object.entries(grouped).map(([cat, list]) => (
              <div key={cat}>
                <h2 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">{cat}</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {list.map((t) => (
                    <TemplateCard key={t.id} template={t}
                      onEdit={() => setEditing({ ...t, _mode: 'edit' })}
                      onArchive={() => archive(t)} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editing && (
        <EditorModal
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }}
        />
      )}
    </div>
  );
}

function TemplateCard({ template, onEdit, onArchive }) {
  const preview = template.body.length > 140 ? template.body.slice(0, 140) + '…' : template.body;
  return (
    <div className={`bg-white rounded-xl border p-3 hover:shadow-sm ${template.is_active === false ? 'border-gray-200 opacity-70' : 'border-blue-200'}`} data-testid={`staff-sms-template-card-${template.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-bold text-gray-900 truncate">{template.name}</h3>
            {template.is_active === false && (
              <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-gray-200 text-gray-600 inline-flex items-center gap-0.5">
                <EyeOff className="w-3 h-3" /> Archived
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-1 shrink-0">
          <button onClick={onEdit} className="text-xs font-semibold text-blue-700 hover:text-blue-900 px-2 py-1" data-testid={`staff-sms-template-edit-${template.id}`}>Edit</button>
          {template.is_active !== false && (
            <button onClick={onArchive} className="text-gray-400 hover:text-red-600 p-1" title="Archive" data-testid={`staff-sms-template-archive-${template.id}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      <p className="text-xs text-gray-700 mt-2 whitespace-pre-wrap font-mono bg-gray-50 rounded p-2 border border-gray-100">
        {preview}
      </p>
      {template.placeholders?.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {template.placeholders.map((p) => (
            <span key={p} className="inline-flex items-center gap-1 bg-blue-50 border border-blue-200 rounded-full text-[10px] px-2 py-0.5 font-medium text-blue-800">
              {`{{${p}}}`}
            </span>
          ))}
        </div>
      )}
      <div className="text-[10px] text-gray-500 mt-2 border-t pt-1.5">
        {template.body.length} chars · {Math.ceil(template.body.length / 160) || 1} SMS segment{template.body.length > 160 ? 's' : ''}
      </div>
    </div>
  );
}

function EditorModal({ initial, onClose, onSaved }) {
  const isNew = initial._mode === 'new';
  const [name, setName] = useState(initial.name || '');
  const [body, setBody] = useState(initial.body || '');
  const [category, setCategory] = useState(initial.category || '');
  const [saving, setSaving] = useState(false);

  const overLimit = body.length > MAX_BODY_CHARS;
  const canSave = name.trim().length > 0 && body.trim().length > 0 && !overLimit && !saving;

  // Live preview: replace `{{var}}` with `«VAR»` markers so staff see where
  // the variables land without inventing sample data.
  const preview = useMemo(
    () => body.replace(/{{\s*([a-zA-Z0-9_]+)\s*}}/g, (_m, k) => `«${k}»`),
    [body],
  );

  const segments = Math.max(1, Math.ceil(body.length / 160));

  const insertPlaceholder = (name) => {
    const el = document.getElementById('sms-tpl-body');
    const insertion = `{{${name}}}`;
    if (el && typeof el.selectionStart === 'number') {
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const next = body.slice(0, start) + insertion + body.slice(end);
      setBody(next);
      // Reset caret after re-render
      setTimeout(() => {
        el.focus();
        el.selectionStart = el.selectionEnd = start + insertion.length;
      }, 0);
    } else {
      setBody((b) => b + insertion);
    }
  };

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const url = isNew
        ? `${API_URL}/api/messages/sms-templates`
        : `${API_URL}/api/messages/sms-templates/${initial.id}`;
      const res = await fetch(url, {
        method: isNew ? 'POST' : 'PATCH',
        credentials: 'include',
        headers: authHeaders(),
        body: JSON.stringify({ name: name.trim(), body, category: category.trim() || null }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${res.status}`);
      }
      toast.success(isNew ? `Created "${name}"` : `Updated "${name}"`);
      onSaved();
    } catch (e) {
      toast.error('Save failed', { description: String(e.message || e).slice(0, 200) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6" data-testid="staff-sms-template-editor">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[95vh] overflow-hidden flex flex-col shadow-2xl">
        <div className="flex items-center justify-between px-4 py-3 border-b shrink-0">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-blue-600" />
            <h2 className="font-bold text-gray-900">{isNew ? 'New SMS template' : 'Edit SMS template'}</h2>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg" data-testid="staff-sms-template-editor-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-gray-600 mb-1">Name</label>
              <input
                type="text" value={name} onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Delivery today"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none"
                data-testid="staff-sms-template-name"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Category</label>
              <input
                type="text" value={category} onChange={(e) => setCategory(e.target.value)}
                placeholder="e.g. Deliveries"
                list="sms-tpl-categories"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none"
                data-testid="staff-sms-template-category"
              />
              <datalist id="sms-tpl-categories">
                {CATEGORY_SUGGESTIONS.map((c) => <option key={c} value={c} />)}
              </datalist>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-xs font-semibold text-gray-600">Body</label>
              <span className={`text-[11px] font-mono ${overLimit ? 'text-red-600 font-bold' : 'text-gray-500'}`}>
                {body.length}/{MAX_BODY_CHARS} · {segments} segment{segments > 1 ? 's' : ''}
              </span>
            </div>
            <textarea
              id="sms-tpl-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={5}
              placeholder="Hi {{first_name}}, your Ox Resin order {{order_ref}} is out for delivery today."
              className={`w-full border rounded-lg px-3 py-2 text-sm font-mono focus:ring-2 focus:outline-none ${overLimit ? 'border-red-400 focus:ring-red-300' : 'border-gray-300 focus:ring-blue-400'}`}
              data-testid="staff-sms-template-body"
            />
            <div className="flex flex-wrap gap-1.5 mt-2">
              <span className="text-[11px] text-gray-500 flex items-center gap-1 mr-1"><Wand2 className="w-3 h-3" /> Insert:</span>
              {PLACEHOLDER_SUGGESTIONS.map((p) => (
                <button
                  key={p} onClick={() => insertPlaceholder(p)}
                  className="text-[10px] font-mono text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-full px-2 py-0.5"
                  data-testid={`staff-sms-template-insert-${p}`}
                >
                  {`{{${p}}}`}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Live preview</label>
            <div className="rounded-lg border border-blue-200 bg-blue-50/60 px-3 py-2 text-sm whitespace-pre-wrap text-blue-900 min-h-[80px]" data-testid="staff-sms-template-preview">
              {preview || <span className="italic text-blue-400">Type the message body — variables will render as «var».</span>}
            </div>
            <p className="text-[10px] text-gray-500 mt-1">
              Variables shown as <code>«name»</code>. Staff will fill each one in when sending; missing values render as empty strings so customers never see <code>{'{{first_name}}'}</code>.
            </p>
          </div>
        </div>

        <div className="border-t px-4 py-3 flex justify-end gap-2 shrink-0">
          <button onClick={onClose} className="px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 rounded-lg">
            Cancel
          </button>
          <button
            onClick={save} disabled={!canSave}
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white text-sm font-semibold px-3 py-2 rounded-lg"
            data-testid="staff-sms-template-save"
          >
            <Save className="w-4 h-4" /> {saving ? 'Saving…' : (isNew ? 'Create template' : 'Save changes')}
          </button>
        </div>
      </div>
    </div>
  );
}
